'use client';

/**
 * QuoteLockTimer Component
 *
 * Displays a countdown timer for quote lock expiry.
 * Shows visual urgency indicators as time runs out.
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export interface QuoteLockTimerProps {
  /** Lock expiry time */
  expiresAt: Date;
  /** Callback when timer expires */
  onExpire?: () => void;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Whether to show compact version */
  compact?: boolean;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

// ============================================================================
// Icon Components
// ============================================================================

const ClockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $urgency: 'normal' | 'warning' | 'critical';
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $urgency, $neonAccent }) => {
  const getBackgroundColor = () => {
    switch ($urgency) {
      case 'critical':
        return $isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)';
      case 'warning':
        return $isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)';
      default:
        if ($neonAccent) {
          const neonStyles = getNeonStyles($neonAccent, $isDark);
          return neonStyles.background;
        }
        return $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
    }
  };

  const getBorderColor = () => {
    switch ($urgency) {
      case 'critical':
        return $isDark ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.3)';
      case 'warning':
        return $isDark ? 'rgba(245, 158, 11, 0.35)' : 'rgba(245, 158, 11, 0.25)';
      default:
        if ($neonAccent) {
          const neonStyles = getNeonStyles($neonAccent, $isDark);
          return neonStyles.border;
        }
        return $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
    }
  };

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    background: getBackgroundColor(),
    border: `1px solid ${getBorderColor()}`,
  };
});

const TimeDigit = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $urgency: 'normal' | 'warning' | 'critical';
}>(({ theme, $isDark, $urgency }) => {
  const getColor = () => {
    switch ($urgency) {
      case 'critical':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.primary;
    }
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 28,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
    fontWeight: 600,
    fontSize: '0.875rem',
    fontVariantNumeric: 'tabular-nums',
    color: getColor(),
  };
});

const TimeSeparator = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.875rem',
  opacity: 0.5,
});

// ============================================================================
// Utility Functions
// ============================================================================

function calculateTimeRemaining(expiresAt: Date): TimeRemaining {
  const now = Date.now();
  const target = new Date(expiresAt).getTime();
  const total = Math.max(0, target - now);

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / (1000 * 60)) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, total };
}

function getUrgencyLevel(timeRemaining: TimeRemaining): 'normal' | 'warning' | 'critical' {
  const totalMinutes = timeRemaining.total / (1000 * 60);

  if (totalMinutes <= 5) return 'critical';
  if (totalMinutes <= 30) return 'warning';
  return 'normal';
}

function padZero(num: number): string {
  return num.toString().padStart(2, '0');
}

// ============================================================================
// Component
// ============================================================================

export const QuoteLockTimer: React.FC<QuoteLockTimerProps> = ({ expiresAt, onExpire, neonAccent, compact = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() => calculateTimeRemaining(expiresAt));

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(expiresAt);
      setTimeRemaining(remaining);

      if (remaining.total <= 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  const urgency = getUrgencyLevel(timeRemaining);
  const isExpired = timeRemaining.total <= 0;

  const getIconColor = () => {
    switch (urgency) {
      case 'critical':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return neonAccent ? getNeonStyles(neonAccent, isDark).base : theme.palette.text.secondary;
    }
  };

  if (isExpired) {
    return (
      <StyledContainer $isDark={isDark} $urgency="critical" $neonAccent={neonAccent}>
        <ClockIcon size={18} color={theme.palette.error.main} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
          Lock Expired
        </Typography>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer
      $isDark={isDark}
      $urgency={urgency}
      $neonAccent={neonAccent}
      animate={
        urgency === 'critical'
          ? {
              scale: [1, 1.02, 1],
            }
          : undefined
      }
      transition={
        urgency === 'critical'
          ? {
              duration: 1,
              repeat: Infinity,
              ease: 'easeInOut',
            }
          : undefined
      }
    >
      <ClockIcon size={18} color={getIconColor()} />

      {!compact && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Lock expires in
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {timeRemaining.days > 0 && (
          <>
            <TimeDigit $isDark={isDark} $urgency={urgency}>
              {timeRemaining.days}d
            </TimeDigit>
            <TimeSeparator>:</TimeSeparator>
          </>
        )}
        <TimeDigit $isDark={isDark} $urgency={urgency}>
          {padZero(timeRemaining.hours)}
        </TimeDigit>
        <TimeSeparator>:</TimeSeparator>
        <TimeDigit $isDark={isDark} $urgency={urgency}>
          {padZero(timeRemaining.minutes)}
        </TimeDigit>
        <TimeSeparator>:</TimeSeparator>
        <TimeDigit $isDark={isDark} $urgency={urgency}>
          {padZero(timeRemaining.seconds)}
        </TimeDigit>
      </Box>
    </StyledContainer>
  );
};

QuoteLockTimer.displayName = 'QuoteLockTimer';

export default QuoteLockTimer;
